import { cn, focusHaloFlush, HapticSurface, panelMotion, press } from "@pax/ui";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Plus } from "lucide-react";
import { useSession } from "@/features/auth/queries";
import { formatCredits } from "../format";
import { creditBalanceQuery } from "../queries";
import { useTopUp } from "../top-up";
import { Coin } from "./Coin";

const pill = `relative flex h-8 shrink-0 items-center gap-2 rounded-full border border-foreground/70 bg-background pr-3 pl-1.5 text-sm font-medium tabular-nums hover:bg-foreground/[0.07] aria-expanded:bg-foreground/[0.07] ${panelMotion} ${press} ${focusHaloFlush}`;

const plus = `relative grid size-8 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground hover:bg-primary/80 ${panelMotion} ${press} ${focusHaloFlush}`;

/**
 * The credits you can spend, and the way into what granted and spent them.
 * Across the foot of the sidebar it is a whole row; in the bar it is only as
 * wide as its figure.
 */
export function CreditsPill({
  row = false,
  open,
  onToggle,
  className,
}: {
  row?: boolean;
  open: boolean;
  onToggle: () => void;
  className?: string;
}) {
  const { data: session } = useSession();
  const signedIn = Boolean(session?.user);
  const { data: credits } = useQuery({
    ...creditBalanceQuery(session?.user.id ?? ""),
    enabled: signedIn,
  });
  const { openTopUp } = useTopUp();

  if (!signedIn) return null;

  const balance = credits === undefined ? "–" : formatCredits(credits);

  return (
    <div className={cn("flex shrink-0 items-center gap-1.5", row && "w-full", className)}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-label={`${balance} credits`}
        className={cn(pill, row && "min-w-0 flex-1")}
      >
        <Coin />
        <span>{balance}</span>
        {row && <ChevronRight aria-hidden className="ml-auto size-4 text-muted-foreground" />}
        <HapticSurface />
      </button>
      <button type="button" onClick={openTopUp} aria-label="Top up credits" className={plus}>
        <Plus aria-hidden className="size-4" />
        <HapticSurface />
      </button>
    </div>
  );
}
